import { Injectable, NgZone, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, tap, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ImageService } from './image.service';

import { BlogPost, PaginationInfo } from '../models/blog-post.models';
import { Image as ImageModel } from '../models/image-model';

export interface BlogPostsResponse {
  message: string;
  posts: BlogPost[];
  pagination: PaginationInfo;
}

type GetPostsOptions = {
  sort?: 'newest' | 'oldest' | 'mostViews' | 'leastViews';
  startDate?: string;
  endDate?: string;
  published?: boolean;
  search?: string;
};

@Injectable({
  providedIn: 'root'
})
export class BlogService {

  private apiUrl = environment.apiUrl + '/blogs';

  // Cache-Strukturen
  private cachedBlogPosts: {[key: string]: {posts: BlogPost[], pagination: PaginationInfo}} = {};
  private cachedFeaturedPosts: {mostRead: BlogPost[], recommended: BlogPost[]} | null = null;
  private cachedSinglePosts: {[slug: string]: BlogPost} = {};
  private cachedRandomPosts: {[key: string]: {posts: BlogPost[]}} = {};
  private cachedLatestPosts: {[count: number]: BlogPost[]} = {};
  
  // Cache-Einstellungen
  private cacheDuration = 5 * 60 * 1000; // 5 Minuten Cache-Dauer
  private lastCacheTime: {[key: string]: number} = {};
  
  // Für Navigation speichern wir die letzte besuchte Seite
  private lastVisitedPage: number = 1;
  private lastSearchTerm: string = '';
  private lastPageSize: number = 6;

  private isServer: boolean;

  constructor(
    private http: HttpClient,
    private imageService: ImageService,
    private zone: NgZone,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isServer = isPlatformServer(platformId);
  }

  // Hilfslogger: auf dem Server 404 nicht loggen, 4xx warnen, 5xx/Netzwerkfehler error.
  private logHttpError(context: string, error: any, options?: { browser404AsWarn?: boolean }): void {
    const isHttpError = error instanceof HttpErrorResponse;

    // Browser-Logging: wie bisher, außer optional 404 als Warnung
    if (!this.isServer) {
      if (isHttpError && error.status === 404 && options?.browser404AsWarn) {
        console.warn(`${context}: 404 Not Found`, { status: error.status, url: error.url });
      } else {
        console.error(context, error);
      }
      return;
    }

    // Server-Logging (SSR)
    if (!isHttpError) {
      console.error(`SSR: ${context}: Unbekannter Fehler`, error);
      return;
    }

    // 404: auf dem Server nicht loggen (erwartbarer Zustand, z. B. Bots/Crawler)
    if (error.status === 404) {
      return;
    }

    // 5xx oder Netzwerkfehler (status 0): als Error loggen
    if (error.status >= 500 || error.status === 0) {
      console.error(`SSR: ${context}`, { status: error.status, message: error.message, url: error.url });
      return;
    }

    // Sonstige 4xx: warnen
    console.warn(`SSR: ${context}`, { status: error.status, message: error.message, url: error.url });
  }

  createBlogPostByAI(type: string, longtailKeyword?: string, topic?: string){
    return this.http.post<{ message: string, jobId: any, streamToken?: string }>(this.apiUrl + "/createbyai", {type: type, options: {longTailKeyword: longtailKeyword, topic: topic}})
  }

  // SSE-Stream als Observable
  streamCreateBlogByAI(jobId: string, streamToken?: string): Observable<{ type: string; data: any }> {
    return new Observable((observer) => {
      const query = streamToken ? `?streamToken=${encodeURIComponent(streamToken)}` : '';
      const url = `${this.apiUrl}/createbyai/stream/${encodeURIComponent(jobId)}${query}`;
      const es = new EventSource(url, { withCredentials: true });

      const onMessage = (ev: MessageEvent) => {
        try {
          const data = JSON.parse(ev.data);
          this.zone.run(() => observer.next({ type: 'message', data }));
        } catch (e) {
          // Falls mal kein JSON kommt
          this.zone.run(() => observer.next({ type: 'message', data: ev.data }));
        }
      };

      const onStarted = (ev: MessageEvent) => {
        const data = JSON.parse(ev.data);
        this.zone.run(() => observer.next({ type: 'started', data }));
      };
      const onProgress = (ev: MessageEvent) => {
        const data = JSON.parse(ev.data);
        this.zone.run(() => observer.next({ type: 'progress', data }));
      };
      const onDone = (ev: MessageEvent) => {
        const data = JSON.parse(ev.data);
        this.zone.run(() => {
          observer.next({ type: 'done', data });
          observer.complete();
        });
        es.close();
      };

      const onError = (err: any) => {
        this.zone.run(() => observer.error(err));
        es.close();
      };

      es.onmessage = onMessage; // falls der Server auch default "message" schickt
      es.addEventListener('started', onStarted as EventListener);
      es.addEventListener('progress', onProgress as EventListener);
      es.addEventListener('done', onDone as EventListener);
      es.onerror = onError;

      return () => {
        es.close();
      };
    });
  }

  // Getter für die letzte Seiten-Position
  getLastPagePosition(): { page: number, search: string, pageSize: number } {
    return {
      page: this.lastVisitedPage,
      search: this.lastSearchTerm,
      pageSize: this.lastPageSize
    };
  }

  // Hilfsmethode zum Transformieren der Bild-URLs in einem BlogPost
  private transformPostImageLinks(post: BlogPost): BlogPost {
    if (!post) return post;
    
    // Deep-Clone des Posts erstellen, um Mutationen zu vermeiden
    const processedPost = JSON.parse(JSON.stringify(post));
    
    // Bilder in den Metadaten verarbeiten, falls vorhanden
    if (Array.isArray(processedPost.imagesMetaData)) {
      processedPost.imagesMetaData.forEach((image: ImageModel, index: number) => {
        if (image && image.link) {
          processedPost.imagesMetaData[index].link = this.imageService.transformImageLink(image.link);
        }
      });
    }
    
    // imageMetaDataMap aktualisieren
    if (processedPost.imageMetaData) {
      Object.keys(processedPost.imageMetaData).forEach(key => {
        const image = processedPost.imageMetaData[key];
        if (image && image.link) {
          processedPost.imageMetaData[key].link = this.imageService.transformImageLink(image.link);
        }
      });
    }
    
    // jsonLd-Bild aktualisieren
    if (processedPost.jsonLd && processedPost.jsonLd.image && processedPost.jsonLd.image.url) {
      // Prüfen, ob die URL nur eine ID ist
      if (processedPost.jsonLd.image.url.match(/^[0-9a-f]{24}$/)) {
        // Suchen wir die entsprechende Bild-URL in imagesMetaData
        const imageMetaData = processedPost.imagesMetaData?.find((img: ImageModel) => img._id === processedPost.jsonLd.image.url);
        if (imageMetaData && imageMetaData.link) {
          processedPost.jsonLd.image.url = this.imageService.transformImageLink(imageMetaData.link);
        }
      } else {
        // Falls bereits ein Pfad vorhanden ist, transformieren wir diesen
        processedPost.jsonLd.image.url = this.imageService.transformImageLink(processedPost.jsonLd.image.url);
      }
    }
    
    return processedPost;
  }

  getBlogPosts(
    page: number = 1,
    limit: number = 10,
    opts?: string | GetPostsOptions
  ): Observable<{posts: BlogPost[], pagination: PaginationInfo}> {
    // Speichern der aktuellen Navigationsparameter
    this.lastVisitedPage = page;

    // Normalisiere Optionen
    let options: GetPostsOptions = {};
    if (typeof opts === 'string') {
      options.search = opts;
    } else if (opts && typeof opts === 'object') {
      options = { ...opts };
    }
    this.lastSearchTerm = options.search || '';
    this.lastPageSize = limit;
    
    // Cache-Key erstellen (alle relevanten Optionen berücksichtigen)
    const cacheKey = [
      `page_${page}`,
      `limit_${limit}`,
      `sort_${options.sort || ''}`,
      `start_${options.startDate || ''}`,
      `end_${options.endDate || ''}`,
      `published_${typeof options.published === 'boolean' ? options.published : ''}`,
      `search_${options.search || ''}`
    ].join('|');
    
    // Prüfe Cache
    if (this.cachedBlogPosts[cacheKey] && 
        (Date.now() - (this.lastCacheTime[cacheKey] || 0)) < this.cacheDuration) {
      return of(this.cachedBlogPosts[cacheKey]);
    }
    
    // HTTP-Parameter
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString())
      .set('includeimageMetaData', 'true');
    
    if (options.search && options.search.trim() !== '') {
      params = params.set('search', options.search);
    }
    if (options.sort) {
      params = params.set('sort', options.sort);
    }
    if (options.startDate) {
      params = params.set('startDate', options.startDate);
    }
    if (options.endDate) {
      params = params.set('endDate', options.endDate);
    }
    if (typeof options.published === 'boolean') {
      params = params.set('published', String(options.published));
    }
    
    // HTTP-Anfrage
    return this.http.get<BlogPostsResponse>(this.apiUrl, { params })
      .pipe(
        map(response => {
          // Transformiere die Bild-URLs in jedem Post
          const transformedPosts = response.posts.map(post => this.transformPostImageLinks(post));
          return {
            posts: transformedPosts,
            pagination: response.pagination
          };
        }),
        tap(response => {
          // Speichere im Cache
          this.cachedBlogPosts[cacheKey] = response;
          this.lastCacheTime[cacheKey] = Date.now();
        }),
        shareReplay(1),
        catchError((error: HttpErrorResponse) => {
          this.logHttpError('API Fehler: Blog-Posts konnten nicht abgerufen werden', error);
          return throwError(() => new Error('Fehler beim Laden der Blog-Beiträge'));
        })
      );
  }
  
  getFeaturedPosts(limit: number = 4): Observable<{mostRead: BlogPost[], recommended: BlogPost[]}> {
    const cacheKey = `featured_${limit}`;
    
    if (this.cachedFeaturedPosts && 
        (Date.now() - (this.lastCacheTime[cacheKey] || 0)) < this.cacheDuration) {
      return of(this.cachedFeaturedPosts);
    }
    
    let params = new HttpParams()
      .set('limit', limit.toString())
      .set('includeimageMetaData', 'true');
    
    return this.http.get<{
      message: string, 
      mostRead: BlogPost[], 
      recommended: BlogPost[]
    }>(`${this.apiUrl}/featured`, { params })
      .pipe(
        map(response => {
          return {
            mostRead: response.mostRead.map(post => this.transformPostImageLinks(post)),
            recommended: response.recommended.map(post => this.transformPostImageLinks(post))
          };
        }),
        tap(response => {
          this.cachedFeaturedPosts = response;
          this.lastCacheTime[cacheKey] = Date.now();
        }),
        shareReplay(1),
        catchError((error: HttpErrorResponse) => {
          this.logHttpError('API Fehler: Featured Posts konnten nicht abgerufen werden', error);
          return throwError(() => new Error('Fehler beim Laden der Featured-Beiträge'));
        })
      );
  }

  getRandomPosts(topic: string, limit: number = 3): Observable<{posts: BlogPost[]}> {
    const cacheKey = `random_${topic}_${limit}`;
    
    if (this.cachedRandomPosts[cacheKey] && 
        (Date.now() - (this.lastCacheTime[cacheKey] || 0)) < this.cacheDuration) {
      return of(this.cachedRandomPosts[cacheKey]);
    }
    
    let params = new HttpParams()
      .set('topic', topic)
      .set('limit', limit.toString())
      .set('includeimageMetaData', 'true');
    
    return this.http.get<{
      message: string, 
      posts: BlogPost[]
    }>(`${this.apiUrl}/random`, { params })
      .pipe(
        map(response => {
          return {
            posts: response.posts.map(post => this.transformPostImageLinks(post)),
          };
        }),
        tap(response => {
          this.cachedRandomPosts[cacheKey] = response;
          this.lastCacheTime[cacheKey] = Date.now();
        }),
        shareReplay(1),
        catchError((error: HttpErrorResponse) => {
          this.logHttpError('API Fehler: Zufällige Posts konnten nicht abgerufen werden', error);
          return throwError(() => new Error('Fehler beim Laden der zufälligen Beiträge'));
        })
      );
  }

  // Zusätzliche Methode für die Homepage (nur neueste Posts)
  getLatestBlogPosts(count: number = 3): Observable<BlogPost[]> {
    const cacheKey = `latest_${count}`;
    
    if (this.cachedLatestPosts[count] && 
        (Date.now() - (this.lastCacheTime[cacheKey] || 0)) < this.cacheDuration) {
      return of(this.cachedLatestPosts[count]);
    }
    
    return this.getBlogPosts(1, count).pipe(
      map(response => response.posts),
      tap(posts => {
        this.cachedLatestPosts[count] = posts;
        this.lastCacheTime[cacheKey] = Date.now();
      })
    );
  }
  
  // Holt einen einzelnen Blog-Beitrag anhand des Slugs
  getBlogPostBySlug(slug: string): Observable<BlogPost | null> {
    const cacheKey = `slug_${slug}`;
    
    if (this.cachedSinglePosts[slug] && 
        (Date.now() - (this.lastCacheTime[cacheKey] || 0)) < this.cacheDuration) {
      return of(this.cachedSinglePosts[slug]);
    }
    
    return this.http.get<{message: string, post: BlogPost}>(`${this.apiUrl}/${slug}`)
      .pipe(
        map(response => this.transformPostImageLinks(response.post)),
        tap(post => {
          this.cachedSinglePosts[slug] = post;
          this.lastCacheTime[cacheKey] = Date.now();
        }),
        shareReplay(1),
        catchError((error: HttpErrorResponse) => {
          // Erwartbare 404: im SSR nicht loggen, im Browser nur warnen
          this.logHttpError(`Fehler beim Abrufen des Blog-Posts mit Slug ${slug}`, error, { browser404AsWarn: true });
          return throwError(() => error);
        })
      );
  }

  // Holt einen einzelnen Blog-Beitrag anhand der ID
  getBlogPostById(id: string): Observable<BlogPost | null> {
    return this.http.get<{message: string, post: BlogPost}>(`${this.apiUrl}/${id}`)
      .pipe(
        map(response => this.transformPostImageLinks(response.post)),
        catchError((error: HttpErrorResponse) => {
          this.logHttpError(`Fehler beim Abrufen des Blog-Posts mit ID: ${id}`, error);
          return throwError(() => error);
        })
      );
  }

  // Speichert einen neuen Blog-Beitrag
  saveBlogPost(blogPost: BlogPost): Observable<BlogPost> {
    return this.http.post<{message: string, post: BlogPost}>(this.apiUrl, blogPost)
      .pipe(
        map(response => {
          console.log('Blog-Post erfolgreich gespeichert:', response.message);
          this.clearCache(); // Cache nach Änderungen zurücksetzen
          return this.transformPostImageLinks(response.post);
        }),
        catchError((error: HttpErrorResponse) => {
          this.logHttpError('Fehler beim Speichern des Blog-Posts', error);
          return throwError(() => error);
        })
      );
  }

  // Aktualisiert einen vorhandenen Blog-Beitrag
  updateBlogPost(id: string, blogPost: BlogPost): Observable<BlogPost> {
    return this.http.put<{message: string, post: BlogPost}>(`${this.apiUrl}/${id}`, blogPost)
      .pipe(
        map(response => {
          console.log('Blog-Post erfolgreich aktualisiert:', response.message);
          this.clearCache(); // Cache nach Änderungen zurücksetzen
          return this.transformPostImageLinks(response.post);
        }),
        catchError((error: HttpErrorResponse) => {
          this.logHttpError('Fehler beim Aktualisieren des Blog-Posts', error);
          return throwError(() => error);
        })
      );
  }

  refreshPublishReview(id: string): Observable<BlogPost> {
    return this.http.post<{message: string, post: BlogPost}>(`${this.apiUrl}/${id}/review-publish`, {})
      .pipe(
        map(response => {
          console.log('Publish-Review erfolgreich aktualisiert:', response.message);
          this.clearCache();
          return this.transformPostImageLinks(response.post);
        }),
        catchError((error: HttpErrorResponse) => {
          this.logHttpError('Fehler beim Aktualisieren des Publish-Reviews', error);
          return throwError(() => error);
        })
      );
  }

  // Löscht einen Blog-Beitrag
  deleteBlogPost(id: string): Observable<void> {
    return this.http.delete<{message: string}>(`${this.apiUrl}/${id}`)
      .pipe(
        map(response => {
          console.log('Blog-Post erfolgreich gelöscht:', response.message);
          this.clearCache(); // Cache nach Änderungen zurücksetzen
        }),
        catchError((error: HttpErrorResponse) => {
          this.logHttpError('Fehler beim Löschen des Blog-Posts', error);
          return throwError(() => error);
        })
      );
  }
  
  // Methode zum manuellen Zurücksetzen des Caches
  clearCache(): void {
    this.cachedBlogPosts = {};
    this.cachedFeaturedPosts = null;
    this.cachedSinglePosts = {};
    this.cachedLatestPosts = {};
    this.lastCacheTime = {};
    console.log('Blog-Service: Cache wurde zurückgesetzt');
  }
}
